import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { NotifyOthersInput } from '../timeoff/NotifyOthersInput';
import { EmployeePicker } from '../timeoff/EmployeePicker';
import {
    createOvertimeRequest,
    getCurrentUserInfo,
    Employee,
    NotifyRecipient,
} from '../dataProvider';
import { getDefaultNotifyRecipient } from '../departmentDefaults';

/**
 * Rounds a duration (in total minutes) up to the nearest 15-minute increment.
 *
 * Examples:
 * - 0 minutes → 0
 * - 1 minute → 15
 * - 15 minutes → 15
 * - 16 minutes → 30
 * - 90 minutes → 90
 * - 91 minutes → 105
 *
 * @param totalMinutes - The raw duration in minutes to round up.
 * @returns The duration rounded up to the nearest 15 minutes.
 */
const roundUpToNearest15 = (totalMinutes: number): number => {
    if (totalMinutes <= 0) return 0;
    return Math.ceil(totalMinutes / 15) * 15;
};

/**
 * Parses a time string in "HH:MM" format and returns total minutes since midnight.
 *
 * @param time - A time string in "HH:MM" format (e.g., "09:30").
 * @returns Total minutes since midnight, or NaN if the string is invalid.
 */
const parseTimeToMinutes = (time: string): number => {
    const parts = time.split(':');
    if (parts.length !== 2) return NaN;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return NaN;
    return hours * 60 + minutes;
};

/**
 * OvertimeApply component for declaring overtime hours.
 *
 * Provides a form with a date picker, start time and end time inputs (stepping in
 * 15-minute increments), task description, optional notification recipients, and an
 * admin-only employee picker for "on behalf of" submissions.
 *
 * The component derives `duration_hours` and `duration_minutes` from the selected
 * start/end time range, rounding the total duration up to the nearest 15 minutes.
 * The backend API contract (`duration_hours`, `duration_minutes`) remains unchanged.
 *
 * When total duration is >= 8 hours, a confirmation dialog is shown before
 * submitting the request.
 */
export const OvertimeApply: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [onBehalfOf, setOnBehalfOf] = useState<Employee | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const canApplyOnBehalf = Array.isArray(permissions) && (
        permissions.includes('overtime.create_on_behalf')
    );

    const [formData, setFormData] = useState({
        date: new Date().toISOString().slice(0, 10),
        start_time: '',
        end_time: '',
        task_description: '',
        notify_others: [] as NotifyRecipient[],
    });

    // On mount, fetch the current user's department and pre-populate notify_others
    // with the department's default contact. The user may remove or add recipients.
    useEffect(() => {
        const fetchDepartmentDefault = async () => {
            try {
                const userInfo = await getCurrentUserInfo();
                const defaultRecipient = getDefaultNotifyRecipient(userInfo?.department);
                if (defaultRecipient) {
                    setFormData(prev => ({ ...prev, notify_others: [defaultRecipient] }));
                }
            } catch (err) {
                // Silently ignore — pre-population is best-effort
                console.error('Failed to fetch current user department:', err);
            }
        };
        fetchDepartmentDefault();
    }, []);

    /**
     * Computes the rounded-up duration from the selected time range.
     *
     * @returns An object containing `duration_hours` and `duration_minutes`, or
     *   `null` if the time range is invalid (end time is not after start time).
     */
    const getComputedDuration = (): { duration_hours: number; duration_minutes: 0 | 15 | 30 | 45; totalMinutes: number } | null => {
        if (!formData.start_time || !formData.end_time) return null;

        const startMinutes = parseTimeToMinutes(formData.start_time);
        const endMinutes = parseTimeToMinutes(formData.end_time);

        if (isNaN(startMinutes) || isNaN(endMinutes)) return null;
        if (endMinutes <= startMinutes) return null;

        const rawDurationMinutes = endMinutes - startMinutes;
        const roundedMinutes = roundUpToNearest15(rawDurationMinutes);

        const duration_hours = Math.floor(roundedMinutes / 60);
        const duration_minutes = (roundedMinutes % 60) as 0 | 15 | 30 | 45;

        return { duration_hours, duration_minutes, totalMinutes: roundedMinutes };
    };

    /**
     * Calculates the total overtime duration in hours using the computed duration.
     *
     * @returns The total duration as a decimal number, or 0 if invalid.
     */
    const getTotalDuration = (): number => {
        const computed = getComputedDuration();
        if (!computed) return 0;
        return computed.duration_hours + computed.duration_minutes / 60;
    };

    /**
     * Validates the form and either opens the confirmation dialog (for >= 8h)
     * or submits directly.
     *
     * @param e - The form submit event.
     */
    const handleSubmitAttempt = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.start_time || !formData.end_time) {
            setError('Please select both start time and end time');
            return;
        }

        const startMinutes = parseTimeToMinutes(formData.start_time);
        const endMinutes = parseTimeToMinutes(formData.end_time);

        if (endMinutes <= startMinutes) {
            setError('End time must be after start time');
            return;
        }

        const total = getTotalDuration();

        if (total <= 0) {
            setError('Total duration must be greater than 0');
            return;
        }
        if (total >= 24) {
            setError('Total duration must be less than 24 hours');
            return;
        }

        if (total >= 8) {
            setConfirmDialogOpen(true);
        } else {
            doSubmit(false);
        }
    };

    /**
     * Submits the overtime request to the API.
     *
     * @param confirmed - Whether the user has explicitly confirmed a >= 8h declaration.
     */
    const doSubmit = async (confirmed: boolean) => {
        setLoading(true);
        setError(null);

        try {
            const computed = getComputedDuration();
            if (!computed) {
                setError('Invalid time range');
                return;
            }

            const payload: Record<string, unknown> = {
                date: formData.date,
                start_time: formData.start_time,
                duration_hours: computed.duration_hours,
                duration_minutes: computed.duration_minutes,
                task_description: formData.task_description,
                notify_others: formData.notify_others.length > 0
                    ? JSON.stringify(formData.notify_others.map(r => r.employee_id ?? r.email))
                    : null,
            };

            if (confirmed) {
                payload.confirmed = true;
            }

            if (onBehalfOf) {
                payload.on_behalf_of = onBehalfOf.employee_id;
            }

            await createOvertimeRequest(payload);

            notify('Overtime declaration submitted successfully', { type: 'success' });
            navigate('/overtime');
        } catch (err: unknown) {
            const errorObj = err as { body?: { message?: string }; message?: string };
            const errorMessage = errorObj?.body?.message || errorObj?.message || 'Failed to submit overtime declaration';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
        }
    };

    const computed = getComputedDuration();
    const hasInvalidRange = formData.start_time && formData.end_time
        && parseTimeToMinutes(formData.end_time) <= parseTimeToMinutes(formData.start_time);
    const totalDuration = getTotalDuration();

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Declare Overtime
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmitAttempt}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        {canApplyOnBehalf && (
                            <EmployeePicker
                                label="Declare on Behalf of (Optional)"
                                value={onBehalfOf}
                                onChange={(emp) => setOnBehalfOf(emp)}
                                disabled={loading}
                            />
                        )}

                        <TextField
                            id="overtime-date"
                            label="Date"
                            type="date"
                            required
                            fullWidth
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'aria-label': 'Date' }}
                            disabled={loading}
                        />

                        <Box display="flex" gap={2}>
                            <TextField
                                id="overtime-start-time"
                                label="Start Time"
                                type="time"
                                required
                                fullWidth
                                value={formData.start_time}
                                onChange={(e) => {
                                    setError(null);
                                    setFormData({ ...formData, start_time: e.target.value });
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 900, 'aria-label': 'Start Time' }}
                                disabled={loading}
                            />
                            <TextField
                                id="overtime-end-time"
                                label="End Time"
                                type="time"
                                required
                                fullWidth
                                value={formData.end_time}
                                onChange={(e) => {
                                    setError(null);
                                    setFormData({ ...formData, end_time: e.target.value });
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 900, 'aria-label': 'End Time' }}
                                disabled={loading}
                            />
                        </Box>

                        {hasInvalidRange && (
                            <Alert severity="error">
                                End time must be after start time
                            </Alert>
                        )}

                        {computed && totalDuration > 0 && (
                            <Alert severity={totalDuration >= 8 ? 'warning' : 'info'}>
                                Total overtime: {computed.duration_hours}h {computed.duration_minutes > 0 ? `${computed.duration_minutes}m` : ''}
                                {totalDuration >= 8 && ' — Confirmation will be required'}
                            </Alert>
                        )}

                        <TextField
                            id="overtime-task-description"
                            label="Task Description"
                            multiline
                            rows={3}
                            required
                            fullWidth
                            value={formData.task_description}
                            onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                            inputProps={{ 'aria-label': 'Task Description' }}
                            disabled={loading}
                        />

                        <NotifyOthersInput
                            value={formData.notify_others}
                            onChange={(value) => setFormData({ ...formData, notify_others: value })}
                            disabled={loading}
                        />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/overtime')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || !formData.date || !formData.task_description || totalDuration <= 0 || !!hasInvalidRange}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Submit Declaration'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </CardContent>

            {/* Confirmation dialog for >= 8h overtime */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
            >
                <DialogTitle>Confirm Overtime Declaration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are declaring {computed ? `${computed.duration_hours}h ${computed.duration_minutes > 0 ? `${computed.duration_minutes}m` : ''}` : ''} of overtime.
                        Declarations of 8 hours or more require explicit confirmation.
                        Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={() => doSubmit(true)} color="primary" autoFocus>
                        Confirm &amp; Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
